// Shared FilterBar state for the admin pages (Dashboard / Insights /
// Conversations). Lifted out so the same FilterBar can ride the header
// across all three pages — set search/tech/date/signed once, and the
// state survives navigation between them. Dashboard + Insights pass the
// filter values through to their queries; Conversations is the canonical
// table view.

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import type { DateBasis, SignedStatus, TeamMember } from "../api/client";
import { PeriodPicker } from "../components/ownerUi";

export interface AdminFilters {
  q: string;
  from: Date | null;
  to: Date | null;
  signed: SignedStatus;
  // Tech filter is multi-select: empty array = "Any tech".
  chargerUserIds: number[];
  // Date basis: bucket/filter by completion (default) or intake. Shared across
  // every admin header via the DateBasisToggle below.
  dateBasis: DateBasis;
}

export const EMPTY_FILTERS: AdminFilters = {
  q: "",
  from: null,
  to: null,
  signed: "any",
  chargerUserIds: [],
  dateBasis: "complete",
};

// Persist the FilterBar across reloads so reopening the dashboard keeps the
// chosen date range / tech / signed filter instead of snapping back to the
// period-only default (which read as a wrong "2-day" request on first open).
// Dates are stored as ISO strings; `q` (transient search text) is intentionally
// NOT persisted — a stale search on reopen is more surprising than helpful.
const FILTERS_STORAGE_KEY = "admin_filters";

function loadFilters(): AdminFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_FILTERS;
    const p = JSON.parse(raw) as {
      from?: string | null; to?: string | null; signed?: SignedStatus;
      chargerUserIds?: number[]; chargerUserId?: number | null; dateBasis?: DateBasis;
    };
    // Back-compat: older saved state stored a single chargerUserId.
    const ids = Array.isArray(p.chargerUserIds)
      ? p.chargerUserIds
      : (p.chargerUserId ? [p.chargerUserId] : []);
    return {
      q: "",
      from: p.from ? new Date(p.from) : null,
      to: p.to ? new Date(p.to) : null,
      signed: p.signed ?? "any",
      chargerUserIds: ids,
      dateBasis: p.dateBasis === "intake" ? "intake" : "complete",
    };
  } catch {
    return EMPTY_FILTERS;
  }
}

function saveFilters(f: AdminFilters): void {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        from: f.from ? f.from.toISOString() : null,
        to: f.to ? f.to.toISOString() : null,
        signed: f.signed,
        chargerUserIds: f.chargerUserIds,
        dateBasis: f.dateBasis,
      }),
    );
  } catch { /* SSR / private mode */ }
}

interface FilterCtx {
  filters: AdminFilters;
  setFilters: (next: AdminFilters) => void;
  techs: TeamMember[];
  setTechs: (next: TeamMember[]) => void;
}

const Ctx = createContext<FilterCtx>({
  filters: EMPTY_FILTERS,
  setFilters: () => {},
  techs: [],
  setTechs: () => {},
});

export function FilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFiltersState] = useState<AdminFilters>(loadFilters);
  const [techs, setTechs] = useState<TeamMember[]>([]);
  const setFilters = useCallback((next: AdminFilters) => {
    setFiltersState(next);
    saveFilters(next);
  }, []);
  return (
    <Ctx.Provider value={{ filters, setFilters, techs, setTechs }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAdminFilters() {
  return useContext(Ctx);
}

// Helpers used by callers to translate Date objects + the camelCase
// chargerUserIds field into the snake_case query params the backend
// expects. Centralized so every page formats identically.

// The date range is a CALENDAR day choice (the picker works in local days),
// and the backend filters the bare `intake_date` DATE column. Sending
// .toISOString() turned "May 31 local" into a UTC instant (e.g. 07:00Z), which
// then compared off-by-the-offset against intake_date — picking a day returned
// a UTC-shifted slice. Send the local Y-M-D so "May 31" means intake_date = May 31.
export function toDateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function filtersToApiOpts(f: AdminFilters): {
  q?: string;
  from?: string;
  to?: string;
  signed?: SignedStatus;
  charger_user_ids?: number[];
  date_basis?: DateBasis;
} {
  return {
    q: f.q?.trim() || undefined,
    from: f.from ? toDateParam(f.from) : undefined,
    to: f.to ? toDateParam(f.to) : undefined,
    signed: f.signed !== "any" ? f.signed : undefined,
    charger_user_ids: f.chargerUserIds.length ? f.chargerUserIds : undefined,
    // "complete" is the backend default — only send when overridden to keep URLs clean.
    date_basis: f.dateBasis === "intake" ? "intake" : undefined,
  };
}

// Shared Complete/Intake toggle for every admin header. Self-contained (reads
// the shared FilterContext) so a page just drops <DateBasisToggle /> — the
// value persists + stays consistent across Dashboard / Conversations / Insights
// / Team. Reuses PeriodPicker's segmented styling.
export function DateBasisToggle() {
  const { filters, setFilters } = useAdminFilters();
  return (
    <PeriodPicker
      value={filters.dateBasis}
      onChange={(v) => setFilters({ ...filters, dateBasis: v })}
      options={[
        { label: "Complete", value: "complete" as DateBasis },
        { label: "Intake", value: "intake" as DateBasis },
      ]}
    />
  );
}
